import { GitMerge, GitPullRequest, Layers, LayoutDashboard, ScrollText } from 'lucide-react';
import { useSession } from '@/components/auth/session';
import { LoadError } from '@/components/common/bits';
import { ClaudeMark } from '@/components/common/marks';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Surface } from '@/components/ui/section';
import { RecentActivity } from '@/features/activity/RecentActivity';
import { StatTile } from '@/features/dashboard/StatTile';
import { RunningStrip } from '@/features/fleet/RunningStrip';
import { GateHero } from '@/features/gates/GateHero';
import { GateStack } from '@/features/gates/GateStack';
import { SetupChecklist } from '@/features/onboarding/SetupChecklist';
import { useBriefs, useHealth, useReview, useStats, useTasks } from '@/hooks/use-fleet';
import { briefsAwaitingPlan, openBriefs } from '@/lib/briefs';
import { useFleetFeed } from '@/lib/feed-context';
import { gateCounts, oldestGateMinutes } from '@/lib/fleet';
import { duration } from '@/lib/format';

/**
 * The dashboard.
 *
 * This page was called "Needs you" and it was one of four front doors. The rail
 * listed Needs you, Jobs, Agents and All steps as peers, so opening the hub
 * began with a choice between four places, three of which existed to report a
 * single number: how many jobs are open, how many machines are up, how many
 * steps are running. A person had to decide what to look at before they could
 * look at anything, which is the same failure the six-panel Inbox had, one level
 * up.
 *
 * So there is one front door now, and the other three destinations are tiles on
 * it. Nothing was deleted and no route changed: `/jobs`, `/agents` and `/tasks`
 * are still pages, still linkable, still in the rail under a heading that says
 * they are the second rank. What changed is that the number each of them was
 * being opened for is on this page, and the tile carrying it *is* the way in.
 *
 * The order is still PRODUCT.md's three questions -- what is waiting on me, what
 * are the agents doing, what happened while I was away -- and the first one is
 * still answered before the fold at a size nobody can miss. It is answered in
 * the hero now: the count and the button that files the next job, in the order
 * a person reads them. The hero used to also carry a "Hi, <address>" greeting
 * and a waving hand on the brand gradient (issue #67); the address is already
 * in the corner of the app, so the greeting said nothing a second time worth
 * the decoration, and the gradient was the one surface in the console that did
 * not answer to the theme. The hero is a plain surface now, themed like every
 * other one.
 *
 * Issue #92 put the greeting back, on different terms. The hero used to open
 * on the count of things waiting at 60px whether that count was real or zero,
 * so a healthy hub -- most hubs, most days -- led with a zero as the largest
 * type on the first screen a person saw, while the one thing they could
 * actually do sat off to the side as the smallest control in the band. Now the
 * hero asks which of the two states it is in first: nothing waiting leads with
 * "Good morning, <name>", what the hub is for, and the actions a person can
 * take (file a job, schedule recurring work, enroll a laptop when none is
 * online), with "nothing is waiting on you" demoted to a line underneath;
 * something waiting still leads with the count at 60px and the greeting
 * shrinks to a line above it. Both are the same band and the same surface,
 * ordered by whether the person is needed, so `GateHero` decides which
 * layout to draw rather than `HomePage` choosing for it.
 *
 * The hero's own line used to end with "N running · N queued" -- the exact pair
 * the Steps tile in the band immediately below it already states, in the same
 * units, from the same query. That line is gone from the hero rather than
 * doubled: the count of things waiting on a person is the hero's fact, what is
 * running and queued is the tile band's.
 *
 * Layout: the hero full width, then the tiles as one short band, then the gate
 * list at the full measure, then what is running beside what happened. The
 * band is figures laid low, not laid equal to the page: each tile is still the
 * way in to the destination it names, and the hero directly above it still
 * carries the one number that is allowed to be loud. Stacked in a narrow
 * column beside the gates the four original tiles ran to roughly 440px
 * against a card whose height they never matched, so the column was dead
 * space on a quiet hub and a second thing to scroll past on a busy one. The
 * gate list gets the whole width because it is the subject of the page. The
 * bottom pair sits side by side from `xl` up so that what is running and what
 * happened do not cost two page heights.
 */
export function HomePage() {
  const { session } = useSession();
  const feed = useFleetFeed();
  const stats = useStats();
  const review = useReview();
  const briefs = useBriefs();
  const tasks = useTasks();
  const health = useHealth();

  const reviewQueue = review.data?.queue ?? [];
  const briefRows = briefs.data ?? [];
  const taskRows = tasks.data ?? [];
  /**
   * The dashboard is made of `?? []`, so a hub that is not answering draws the
   * same page as a hub with nothing on it: every tile zero, the gate hero
   * reporting that there is nothing waiting. That is the one wrong thing this
   * page can say, so the first read that failed says so instead, above the
   * rest.
   */
  const broken = [briefs, tasks, review, stats].find((query) => query.isError && !query.data);
  const gates = gateCounts(briefRows, reviewQueue);
  const running = taskRows.filter(
    (task) => task.status === 'running' || task.status === 'reporting',
  ).length;

  const open = openBriefs(briefRows);
  const awaitingPlan = briefsAwaitingPlan(briefRows, taskRows).length;
  const queued = stats.data?.queue.queued ?? 0;
  const workers = stats.data?.workers;
  const merged = review.data?.metrics.merged;
  const prInFlight = stats.data?.prsInFlight;

  return (
    <>
      <ViewHeader
        title="Dashboard"
        icon={LayoutDashboard}
        width="1180px"
        sub={session.isAdmin ? 'the whole hub' : 'your jobs, your agents, your gates'}
      />
      <Page className="max-w-[1180px]">
        <div className="dy-stack">
          {broken ? (
            <Surface>
              <LoadError
                what="the dashboard"
                error={broken.error}
                retry={() => void broken.refetch()}
              />
            </Surface>
          ) : null}

          <GateHero
            gates={gates}
            oldestMinutes={oldestGateMinutes(briefRows, reviewQueue)}
            health={health.data}
            started={briefRows.length > 0}
          />

          <SetupChecklist />

          {/* The numbers the other destinations were being opened for, one band
              under the hero. Two across on a phone, three from `sm`, all five
              from `lg` once there is room for them side by side. "In review"
              is the fifth: what is queued, running and merged said nothing
              about work that has already landed on a branch and is sitting on
              GitHub, which is where a PR spends the time between an agent
              finishing and a human deciding (issue #30). */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile
              label="Jobs"
              icon={ScrollText}
              to="/jobs"
              tone="var(--dy-plan)"
              layout="row"
              value={open.length}
              note={
                awaitingPlan
                  ? `${awaitingPlan} still needs a plan`
                  : `${briefRows.length} filed in all`
              }
            />
            <StatTile
              label="Agents"
              icon={ClaudeMark}
              to="/agents"
              tone="var(--dy-info)"
              layout="row"
              value={workers ? `${workers.busy}/${workers.total}` : '-'}
              note={
                workers
                  ? `${workers.capacity} concurrent slot${workers.capacity === 1 ? '' : 's'}`
                  : 'working of enrolled'
              }
            />
            <StatTile
              label="Tasks"
              icon={Layers}
              to="/tasks"
              search={queued ? { status: 'queued' } : undefined}
              tone="var(--dy-accent)"
              layout="row"
              value={running}
              note={queued ? `${queued} queued behind them` : 'nothing queued'}
            />
            <StatTile
              label="In review"
              icon={GitPullRequest}
              to="/tasks"
              search={
                prInFlight?.ready
                  ? { status: 'gate' }
                  : prInFlight?.draft
                    ? { status: 'running' }
                    : undefined
              }
              tone="var(--dy-gate)"
              layout="row"
              value={prInFlight?.total ?? '-'}
              note={
                prInFlight?.total
                  ? `${prInFlight.draft} draft, ${prInFlight.ready} ready for a person`
                  : 'nothing open on GitHub'
              }
            />
            <StatTile
              label="Merged"
              icon={GitMerge}
              to="/tasks"
              search={{ status: 'closed' }}
              tone="var(--dy-ok)"
              layout="row"
              value={merged?.count ?? '-'}
              note={
                merged?.count
                  ? `median review ${duration(merged.medianMinutes)}`
                  : prInFlight?.total
                    ? `none merged of ${prInFlight.total} open`
                    : 'nothing merged yet'
              }
            />
          </div>

          {/* The subject of the page, at the full measure. */}
          <GateStack
            briefs={briefRows}
            reviews={reviewQueue}
            staleAfterMinutes={review.data?.metrics.staleAfterMinutes}
            loading={briefs.isPending || review.isPending}
          />

          {/* What is running and what happened. When nothing is running the strip
              renders nothing at all, so the activity list takes the full width
              rather than leaving half the row empty. Side by side from `xl` up:
              below that a 26rem column and a task row do not both read. */}
          {running > 0 ? (
            <div className="grid gap-[var(--dy-gap)] xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
              <RunningStrip tasks={taskRows} />
              <RecentActivity items={feed.items} scope={feed.scope} limit={6} />
            </div>
          ) : (
            <RecentActivity items={feed.items} scope={feed.scope} />
          )}
        </div>
      </Page>
    </>
  );
}
